import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { ChapterTag, FadeUp } from "@/components/Reveal";

const Counter = ({ to }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 1.6,
      ease: "easeOut",
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to]);

  return <span ref={ref}>{String(val).padStart(2, "0")}</span>;
};

const PRINCIPLES = [
  { n: "01", title: "Feed every format", copy: "A family dinner, a midnight biryani order, a patient's recovery meal — we serve them all." },
  { n: "02", title: "Own the supply chain", copy: "One sourcing network, one kitchen standard, shared across every brand and contract." },
  { n: "03", title: "Earn trust daily", copy: "Fifteen years of plates served in Ranchi. Trust is the only growth hack we know." },
];

export default function Manifesto() {
  return (
    <section id="manifesto" data-testid="manifesto-section" className="relative bg-cream px-5 py-24 text-obsidian sm:px-8 lg:py-32">
      <div className="mx-auto max-w-7xl">
        <ChapterTag index="00" label="The Manifesto" dark={false} />

        <div className="mt-12 grid grid-cols-1 gap-12 lg:grid-cols-12">
          <FadeUp className="lg:col-span-8">
            <h2 className="font-display text-2xl font-semibold leading-[1.15] tracking-tight sm:text-3xl lg:text-4xl" data-testid="manifesto-headline">
              We are not a restaurant.{" "}
              <span className="font-serif italic font-medium text-flame">
                We are Ranchi's food infrastructure.
              </span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.15} className="lg:col-span-4">
            <p className="text-base leading-relaxed text-slate-600">
              What started in 2010 as a single family restaurant is today a multi-format food
              company — cooking for dining rooms, delivery apps and hospital corridors at the
              same time, from the same heart.
            </p>
          </FadeUp>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-5 md:grid-cols-3" data-testid="manifesto-principles">
          {PRINCIPLES.map((p, i) => (
            <FadeUp key={p.n} delay={i * 0.12}>
              <div className="group h-full rounded-3xl border border-obsidian/10 bg-white p-8 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-flame/40 hover:shadow-[0_20px_50px_rgba(224,90,43,0.12)]">
                <span className="font-mono text-xs font-semibold tracking-[0.3em] text-flame">{p.n}</span>
                <h3 className="mt-4 font-display text-xl font-semibold tracking-tight">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{p.copy}</p>
              </div>
            </FadeUp>
          ))}
        </div>

        <div className="mt-20 grid grid-cols-1 gap-5 md:grid-cols-6" data-testid="traction-grid">
          <FadeUp className="md:col-span-4">
            <div className="flex h-full flex-col justify-between rounded-3xl bg-obsidian p-9 text-cream sm:p-10" data-testid="traction-hospitals-tile">
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-saffron">Institutional Trust</span>
              <div className="mt-8 flex flex-wrap items-end gap-x-6 gap-y-2">
                <span className="font-display text-6xl font-semibold leading-none tracking-tight text-saffron sm:text-7xl">
                  <Counter to={9} />
                </span>
                <p className="max-w-xs pb-1 text-base leading-snug text-slate-300">
                  hospitals in Ranchi run their canteens on Taste Ride kitchens, every single day
                </p>
              </div>
            </div>
          </FadeUp>
          <FadeUp delay={0.1} className="md:col-span-2">
            <div className="flex h-full flex-col justify-between rounded-3xl border border-obsidian/10 bg-white p-8" data-testid="traction-models-tile">
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-slate-500">Operating Models</span>
              <span className="mt-6 font-display text-6xl font-semibold leading-none text-flame">
                <Counter to={3} />
              </span>
            </div>
          </FadeUp>
          {[
            { n: 4, label: "Dine-in outlets across Ranchi", testid: "traction-outlets-tile" },
            { n: 5, label: "Cloud kitchen brands live on delivery apps", testid: "traction-brands-tile" },
            { n: 2, label: "Delivery giants carry our food — Zomato & Swiggy", testid: "traction-aggregators-tile" },
          ].map((t, i) => (
            <FadeUp key={t.testid} delay={0.1 + i * 0.08} className="md:col-span-2">
              <div className="flex h-full flex-col justify-between rounded-3xl border border-obsidian/10 bg-white p-8 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-saffron/50" data-testid={t.testid}>
                <span className="text-sm leading-snug text-slate-600">{t.label}</span>
                <span className="mt-6 font-display text-5xl font-semibold leading-none text-obsidian">
                  <Counter to={t.n} />
                </span>
              </div>
            </FadeUp>
          ))}
          <FadeUp delay={0.2} className="md:col-span-6">
            <div className="flex flex-wrap items-center justify-between gap-4 rounded-3xl bg-wa/10 p-8 ring-1 ring-wa/30" data-testid="traction-whatsapp-tile">
              <div>
                <span className="font-display text-4xl font-semibold text-obsidian">
                  <Counter to={1} />
                </span>
                <span className="ml-3 font-display text-lg font-medium text-slate-700">
                  WhatsApp line, straight to our kitchen
                </span>
              </div>
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-wa">No middleman · No commission</span>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
